import React, {useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useCurrentUserState, useLogout} from 'app/current-user/api';
import {useAccountSettings} from 'app/settings/api';
import {ACCOUNT_SWITCH_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

type Props = {
  onToggle: () => void;
};

export function UserBox({onToggle}: Props): JSX.Element {
  const [areAccountsExpanded, setAccountsExpanded] = useState(false);
  const [requestLogout] = useLogout();
  const currentUser = useCurrentUserState().data!;
  const account_name = useAccountSettings(undefined, {
    refetchOnMountOrArgChange: false,
  }).data!.account_name;

  function toggleAccounts() {
    if (currentUser.num_accounts > 1) {
      setAccountsExpanded(!areAccountsExpanded);
    }
  }

  const UserContainer = currentUser.num_accounts > 1 ? 'a' : 'div';
  return (
    <StyledUserBoxDiv className="">
      <UserContainer className="user-avatar-button" onClick={toggleAccounts}>
        <UserAvatar user={currentUser} />
        <span className="mx-2 flex-grow-1">{currentUser.name}</span>
        {currentUser.num_accounts > 1 ? (
          <span>
            <i className="ml-1 fa fa-angle-down font-weight-bold" />
          </span>
        ) : null}
      </UserContainer>
      {currentUser.num_accounts > 1 ? (
        <div
          className={`accounts-list no-pad ${
            areAccountsExpanded ? 'open' : ''
          }`}
        >
          <div>
            <div className="font-weight-bold">{gt('Current account')}:</div>
            <div>{account_name}</div>
            <FullHTMLLink
              html_url={ACCOUNT_SWITCH_URL}
              onClick={onToggle}
              className="btn btn-xs btn-primary btn-block mt-2"
            >
              {gt('Switch Account')}
            </FullHTMLLink>
          </div>
        </div>
      ) : null}
      <div className="buttons-line">
        <FullHTMLLink
          renderRegularLink={true}
          className="btn btn-xs btn-outline-primary"
          html_url="/accounts/overview/"
        >
          {gt('My Account')}
        </FullHTMLLink>
        <button
          className="btn btn-xs btn-outline-primary"
          onClick={() => requestLogout()}
        >
          {gt('Logout')}
        </button>
      </div>
      {currentUser.is_staff ? (
        <div className="buttons-line mt-2">
          <a className="btn btn-xs btn-outline-light" href="/a/sp-admin">
            {gt('Admin')}
          </a>
        </div>
      ) : null}
    </StyledUserBoxDiv>
  );
}

const StyledUserBoxDiv = styled.div`
    .user-avatar-button {
        display: flex;
        margin-bottom: 1rem;
        align-items: center;

        > span {
            color: ${colors.blueGray};
            font-size: 0.8rem;
        }

        .avatar {
            transition all 250ms;
            border: 2px solid ${colors.blueGray};
        }
    }

    a.user-avatar-button:hover {
        cursor: pointer;
        > span {color: #FFF;}

        .avatar {
            border-color: #FFF;
        }
    }

    .accounts-list {
        max-height: 0;
        overflow: hidden;
        transition: 250ms;
        opacity: 0;

        &.open {
            opacity: 1;
            max-height: 150px;
        }

        > div {
            font-size: 0.8rem;
            background: #FFF;
            margin-bottom: 1rem;
            padding: 10px;
        }
    }
`;
